import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { AuthService } from 'src/app/auth/auth.service';
import { AuthUserType } from 'src/app/auth/auth.types';
import { AppConstants } from 'src/app/shared/app.constants';
import { SearchResultType } from 'src/app/shared/app.enums';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { AppUtils } from 'src/app/shared/app.utils';
import { GeographicFilterRegionModel } from 'src/app/shared/models/geographic-filter-region-model';
import { UtcToLocalPipe } from 'src/app/shared/pipes/utc-to-local.pipe';
import { AccessibilityService } from '../../services/accessibility.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-search-results',
  templateUrl: './search-results.component.html'
})
export class SearchResultsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(Table, { static: false }) dataTable: Table;  
  private _searchResults: ServerModels.SearchResultsViewModel[] = [];
  private _loading: boolean = false;
  private _messageTemplate: string;
  private _loadingStartRow: number;
  private _loadingEndRow: number;

  readonly canView = AppPermissions.canViewCrimeGunDetails;
  readonly canEditCrimeGun = AppPermissions.canEditCrimeGun;
  readonly canViewCrimeGun = AppPermissions.canViewCrimeGun;
  readonly canViewTraceData = AppPermissions.canViewTraceData;

  @Input() showstatus: boolean = false;
  @Input() geographicFilterRegion: GeographicFilterRegionModel = null;
  @Input() showFA: boolean = false;
  @Input() showEVID: boolean = false;
  @Input() showNIBINSite: boolean = false;
  @Input() showGeographic: boolean = false;
  @Input() dateRangeFilterLabel: string = "";
  @Input() selectedNIBINSite: string = "";
  @Input() hideAgencyTag: boolean = false;

  selectedResultRow: ServerModels.SearchResultsViewModel = null;
  cols: any[] = [
    { field: 'CrimeGunNumber', header: 'Crime Gun ID' },
    { field: 'LegacyATFGunNumber', header: 'Legacy Gun ID(s)' },
    { field: 'SiteCode', header: 'NIBIN Site' },
    { field: 'NIBINAcquisitionDate', header: 'NIBIN Acquisition Date' },
    { field: 'LabCaseNumber', subfield: 'ExhibitReferenceNumber', header: 'NIBIN Case #/Exhibit #' },
    { field: 'AgencyName', header: 'LE Agency' },
    { field: 'LECaseNumber', header: 'LE Case #' },
    { field: 'NIBINOccurrenceDate', header: 'NIBIN Occurrence Date' },
    { field: 'CrimeTypeDesc', header: 'Crime Type' },
    { field: 'Caliber', subfield: 'EvidenceClassCode', header: 'Caliber Evidence Category' },
    { field: 'SerialNumber', header: 'Firearm Manufacturer; Model; Serial #' },
    { field: 'FTSIDs', header: 'FTS ID(s)' },
    { field: 'PurchaseDate', header: 'Purchase Date' },
    { field: 'TimeTo1stShooting', header: 'Time to 1st Shooting (# Days)' },
    { field: 'POECount', header: '# Linked Shootings' }
  ];
  rows: number = AppConstants.defaultTableRows;
  searchResultType = SearchResultType;
  tableStateKey: string = 'searchResultTableState';
  readonly exportDataTooltipText = 'To export only specific records, check the box(es) then choose "Selected Record(s)" from the Export to .csv drop down.';

  @Input() set messageTemplate(msg: string) {
    this._messageTemplate = msg;
  }

  get messageTemplate(): string {
    return this._messageTemplate;
  }

  @Input() set loadingStartRow(row: number) {
    this._loadingStartRow = row;
  }

  get loadingStartRow(): number {
    return this._loadingStartRow;
  }

  @Input() set loadingEndRow(row: number) {
    this._loadingEndRow = row;
  }

  get loadingEndRow(): number {
    return this._loadingEndRow;
  }

  @Output() selectedResult = new EventEmitter<{ result: ServerModels.SearchResultsViewModel, resultType: SearchResultType, rowIndex: number }>();

  @Input() set searchResults(results: ServerModels.SearchResultsViewModel[]) {
    this._searchResults = results;
  }

  get searchResults(): ServerModels.SearchResultsViewModel[] {
    return this._searchResults;
  }

  @Input() set loading(b: boolean) {
    this._loading = b;
  }

  get loading(): boolean {
    return this._loading;
  }

  @Input() currentUserType: string;
  readonly hasUserType: AuthUserType[] = ['ATFFieldDivision', 'ATFHQ'];

  constructor(private authService: AuthService,
    private accessibilitySvc: AccessibilityService) { };

  ngOnInit() {
    if (!this.authService.hasRole(['NESS Administrator']) && !this.authService.hasUserType(this.hasUserType)) {
      this.RemoveFieldFromColsArray('PurchaseDate');
    }
  }

  ngOnDestroy() {
    sessionStorage.removeItem(this.tableStateKey);
  }

  ngAfterViewInit() {
    this.accessibilitySvc.updatePrimeNGPaginatorButtons(this.dataTable.el.nativeElement);
  }

  public customMultiSort(sortEvent: SortEvent): void {
    let result = null;
    const idx = this.cols.findIndex(f => f['field'] == sortEvent.field);
    if (idx > -1 && this.cols[idx].subfield) {
      let field = sortEvent.field;
      let subfield = this.cols[idx]['subfield'];
      sortEvent.data.sort((data1, data2) => {
        const value1: string = `${data1[field]}${data1[subfield]}`;
        const value2: string = `${data2[field]}${data2[subfield]}`;

        result = value1.localeCompare(value2);
        return (sortEvent.order * result);
      });
    } else {
      sortEvent.data.sort((data1, data2) => {
        let value1 = data1[sortEvent.field];
        let value2 = data2[sortEvent.field];
        let result = null;

        if (value1 == null && value2 != null)
          result = -1;
        else if (value1 != null && value2 == null)
          result = 1;
        else if (value1 == null && value2 == null)
          result = 0;
        else if (typeof value1 === 'string' && typeof value2 === 'string')
          result = value1.localeCompare(value2);
        else
          result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

        return (sortEvent.order * result);
      });
    }
  }

  public onSearchResultRowSelect(evt: { data: ServerModels.SearchResultsViewModel, index: number, originalEvent: Event, type: SearchResultType }): void {
    this.selectedResultRow = evt.data;
    this.dataTable.cd.detectChanges();
    this.selectedResult.emit({ result: evt.data, resultType: evt.type, rowIndex: evt.index });
  }

  private RemoveFieldFromColsArray(key: string) {
    this.cols.forEach((value, index) => {
      if (value.field == key) this.cols.splice(index, 1);
    });
  }

  public onExportDataSelected(selector: any) {
    let v = selector.value;
    selector.value = '';
    if (v) {
      this.exportCSV({
        selectionOnly: v == 2
      });
    }
  }

  public exportCSV(options?: any) {
    const fn: string = `ballisticEvidenceList.csv`;
    let exportCols = [...this.cols];
    exportCols.shift(); // remove checkbox column
    AppUtils.exportCSV(AppUtils.createCSVString({ dataTable: this.dataTable, columns: exportCols, selectionOnly: (options && options.selectionOnly), csvSeparator: ',', exportFunction: this.prepareExportData }), {
      fileName: fn
    });
  }

  public prepareExportData(cellData: any): string {
    if (cellData.field == 'NIBINAcquisitionDate' || cellData.field == 'NIBINOccurrenceDate') {
      let pipeTransform: UtcToLocalPipe = new UtcToLocalPipe();
      const receptionDate = pipeTransform.transform(cellData.data, 'MM/dd/yyyy ');
      return receptionDate;
    }
    return cellData.data;
  }

  public openCrimeGun(evt: Event, id: string): void {
    evt.preventDefault();
    const token = sessionStorage.getItem(environment.authTokenStorageKey);
    let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id + '?titleText=Search Page&returnUrl=/search';
    let windowObjRef = window.open(url, '_blank');
    windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
  }
}
